import { FC } from 'react'
import { Configure, Hits, HitsPerPage, Index, InstantSearch, InstantSearchSSRProvider } from 'react-instantsearch'

import { defaultConfig, typesenseInstantSearchAdapter } from '../../utils/typesense/config'
import { WithISServerState } from '../../utils/types/misc'

const PageWithInstantSearch = <Props extends {}>(Component: FC<WithISServerState<Props>>) => (
  (props: WithISServerState<Props>): JSX.Element => {
    const {
      serverState,
      ...rest
    } = props
    // Need to cast type to avoid type error from Omit<T>
    // https://github.com/microsoft/TypeScript/issues/35858
    const pageProps = rest as Props & { serverState: never }
    return (
      <InstantSearchSSRProvider {...serverState}>
        <InstantSearch
          indexName="products"
          searchClient={typesenseInstantSearchAdapter.searchClient}
          stalledSearchDelay={250}
          future={{
            preserveSharedStateOnUnmount: true,
          }}
        >
          <Component
            {...pageProps}
          />
        </InstantSearch>
      </InstantSearchSSRProvider>
    )
  })

export default PageWithInstantSearch

export const InstantSearchData: FC<WithISServerState<{
  country: string
  lang: string
  preset?: string | null
  category?: [string, string]
  supplier?: string
}>> = (props) => {
  const {
    serverState,
    country,
    lang,
    preset,
    category,
    supplier,
  } = props
  const [key, value] = category || []
  const basicFacetRefinements = {
    'country-availability': [country.toUpperCase()],
  }
  const facetRefinements = {
    ...basicFacetRefinements,
    ...(supplier && { supplier: [supplier] }),
  }

  const disjunctiveFacetRefinements = {
    ...(key && value && { [key]: [value] }),
  }

  typesenseInstantSearchAdapter.updateConfiguration({
    ...defaultConfig,
    server: {
      ...defaultConfig.server,
      connectionTimeoutSeconds: 5, // Use low timeout so that SSG is not blocker for a long time
    },
    collectionSpecificSearchParameters: {
      ...defaultConfig.collectionSpecificSearchParameters,
      products: {
        preset: `${preset ?? 'default_products'}_${lang}`,
        limit: defaultConfig.collectionSpecificSearchParameters?.products.limit,
        // Default 0 results so that extra generated requests don't add extra hits
        // The <HitsPerPage> below overrides and controls how many actual products are included
        per_page: 0,
      },
    },
  })

  return (
    <InstantSearchSSRProvider {...serverState}>
      <InstantSearch
        indexName="products"
        searchClient={typesenseInstantSearchAdapter.searchClient}
      >
        <HitsPerPage items={[{ label: 'Fewer hits in SSG', value: 16, default: true }]} />
        <Configure
          disjunctiveFacets={[
            `categories.one.${lang}`,
            `categories.two.${lang}`,
            `categories.three.${lang}`,
          ]}
          facetsRefinements={facetRefinements}
          disjunctiveFacetsRefinements={disjunctiveFacetRefinements}
        />
        <Hits />
        <Index
          indexName="products"
          indexId="unrefined_counts"
        >
          <HitsPerPage items={[{ label: 'No hits for count query', value: 1, default: true }]} />
          <Configure
            disjunctiveFacets={[
              `categories.one.${lang}`,
              `categories.two.${lang}`,
              `categories.three.${lang}`,
            ]}
            facetsRefinements={facetRefinements}
          />
        </Index>
      </InstantSearch>
    </InstantSearchSSRProvider>
  )
}
